import { z } from 'zod';

// Fields

export const WishlistGroupIdSchema = z.string().trim().nonempty().max(24);
export const WishlistParticipantIdSchema = z.string().trim().nonempty().max(24);

export const WishlistItemIdSchema = z.string().nonempty();
export const WishlistItemTitleSchema = z.string().trim().nonempty().max(120);
export const WishlistItemUrlSchema = z
  .url()
  .max(2048)
  .refine(url => url.startsWith('https://'), 'URL must use HTTPS');
export const WishlistItemNoteSchema = z.string().trim().nonempty().max(240);

export const WishlistErrorCodes = {
  NOT_FOUND: 'NOT_FOUND',
  LIMIT_REACHED: 'LIMIT_REACHED',
  INVALID_INPUT: 'INVALID_INPUT',
} as const;
export const WishlistErrorCodeSchema = z.enum(WishlistErrorCodes);

export type WishlistErrorCode = z.infer<typeof WishlistErrorCodeSchema>;

// Items

export const WishlistRetailerSchema = z.object({
  id: z.string().nonempty(),
  name: z.string().nonempty(),
});

export const WishlistItemSchema = z.object({
  id: WishlistItemIdSchema,
  title: WishlistItemTitleSchema,
  url: WishlistItemUrlSchema.nullable(),
  note: WishlistItemNoteSchema.nullable(),
  retailer: WishlistRetailerSchema.nullable(),
  createdAt: z.iso.datetime(),
});

export const MatchWishlistItemSchema = WishlistItemSchema.omit({ createdAt: true });

export type WishlistRetailer = z.infer<typeof WishlistRetailerSchema>;
export type WishlistItem = z.infer<typeof WishlistItemSchema>;
export type MatchWishlistItem = z.infer<typeof MatchWishlistItemSchema>;

// GET /wishlist/:groupId/:participantId

export const GetWishlistRequestSchema = z.object({
  groupId: WishlistGroupIdSchema,
  participantId: WishlistParticipantIdSchema,
});

export const GetWishlistSuccessResponseSchema = z.object({
  success: z.literal(true),
  items: z.array(WishlistItemSchema),
});

export const GetWishlistErrorResponseSchema = z.object({
  success: z.literal(false),
  code: z.enum([WishlistErrorCodes.INVALID_INPUT, WishlistErrorCodes.NOT_FOUND]),
});

export const GetWishlistResponseSchema = z.discriminatedUnion('success', [
  GetWishlistSuccessResponseSchema,
  GetWishlistErrorResponseSchema,
]);

export type GetWishlistRequest = z.infer<typeof GetWishlistRequestSchema>;
export type GetWishlistSuccessResponse = z.infer<typeof GetWishlistSuccessResponseSchema>;
export type GetWishlistErrorResponse = z.infer<typeof GetWishlistErrorResponseSchema>;
export type GetWishlistResponse = z.infer<typeof GetWishlistResponseSchema>;

// POST /wishlist

export const WishlistAction = {
  CREATE: 'CREATE',
  SET: 'SET',
  DELETE: 'DELETE',
} as const;
export const WishlistActionSchema = z.enum(WishlistAction);

export const CreateWishlistActionSchema = z.object({
  action: z.literal(WishlistAction.CREATE),
  title: WishlistItemTitleSchema,
  url: WishlistItemUrlSchema.optional(),
  note: WishlistItemNoteSchema.optional(),
});

export const SetWishlistActionSchema = z.object({
  action: z.literal(WishlistAction.SET),
  id: WishlistItemIdSchema,
  title: WishlistItemTitleSchema,
  url: WishlistItemUrlSchema.nullable(),
  note: WishlistItemNoteSchema.nullable(),
});

export const DeleteWishlistActionSchema = z.object({
  action: z.literal(WishlistAction.DELETE),
  id: WishlistItemIdSchema,
});

export const WishlistActionRequestSchema = z.discriminatedUnion('action', [
  CreateWishlistActionSchema,
  SetWishlistActionSchema,
  DeleteWishlistActionSchema,
]);

export const PerformWishlistActionRequestSchema = z.object({
  groupId: WishlistGroupIdSchema,
  participantId: WishlistParticipantIdSchema,
  action: WishlistActionRequestSchema,
});

export const CreateWishlistSuccessResponseSchema = z.object({
  success: z.literal(true),
  action: z.literal(WishlistAction.CREATE),
  item: WishlistItemSchema,
});

export const SetWishlistSuccessResponseSchema = z.object({
  success: z.literal(true),
  action: z.literal(WishlistAction.SET),
  item: WishlistItemSchema,
});

export const DeleteWishlistSuccessResponseSchema = z.object({
  success: z.literal(true),
  action: z.literal(WishlistAction.DELETE),
  id: WishlistItemIdSchema,
});

export const PerformWishlistActionSuccessResponseSchema = z.discriminatedUnion('action', [
  CreateWishlistSuccessResponseSchema,
  SetWishlistSuccessResponseSchema,
  DeleteWishlistSuccessResponseSchema,
]);

export const PerformWishlistActionErrorResponseSchema = z.object({
  success: z.literal(false),
  code: z.enum([
    WishlistErrorCodes.INVALID_INPUT,
    WishlistErrorCodes.NOT_FOUND,
    WishlistErrorCodes.LIMIT_REACHED,
  ]),
});

export const PerformWishlistActionResponseSchema = z.discriminatedUnion('success', [
  PerformWishlistActionSuccessResponseSchema,
  PerformWishlistActionErrorResponseSchema,
]);

export type CreateWishlistAction = z.infer<typeof CreateWishlistActionSchema>;
export type SetWishlistAction = z.infer<typeof SetWishlistActionSchema>;
export type DeleteWishlistAction = z.infer<typeof DeleteWishlistActionSchema>;
export type WishlistActionRequest = z.infer<typeof WishlistActionRequestSchema>;
export type PerformWishlistActionRequest = z.infer<typeof PerformWishlistActionRequestSchema>;
export type CreateWishlistSuccessResponse = z.infer<typeof CreateWishlistSuccessResponseSchema>;
export type SetWishlistSuccessResponse = z.infer<typeof SetWishlistSuccessResponseSchema>;
export type DeleteWishlistSuccessResponse = z.infer<typeof DeleteWishlistSuccessResponseSchema>;
export type PerformWishlistActionSuccessResponse = z.infer<
  typeof PerformWishlistActionSuccessResponseSchema
>;
export type PerformWishlistActionErrorResponse = z.infer<
  typeof PerformWishlistActionErrorResponseSchema
>;
export type PerformWishlistActionResponse = z.infer<typeof PerformWishlistActionResponseSchema>;
